import { css, StyleSheet } from 'aphrodite';
import * as Api from '@ViewModels';
import { Button } from '../../../components-v2/Button';
import { Dialog, DialogBody, DialogPopup, DialogTitle } from '../../../components-v2/Dialog';
import { THEME } from '../../../components-v2/Theme/theme';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useRefreshStaleSchedules } from '../hooks/useRefreshStaleSchedules';
import type { StaleScheduleErrorWithRepair } from '../hooks/usePublishAutomation';

interface RefreshTimingBannerProps {
	staleScheduleRepairs: StaleScheduleErrorWithRepair[];
	/** Optional only because `template?.id` from the canvas may be undefined briefly during initial load — banner is suppressed in that window. */
	rootTemplateId: string | undefined;
	canEdit: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Fired after the refresh loop lands. Receives the per-step failure list so the host
	 * can distinguish success (all PUTs landed → safe to hard-reload, which is how we
	 * guarantee the canvas re-derives against fresh data) from partial failure (keep the
	 * banner visible with a Try again CTA).
	 */
	onRefreshComplete?: (result: { failedStepIds: string[] }) => void;
}

/**
 * One-click repair banner for automations whose switch-branch child steps have a
 * persisted schedule.criteria that doesn't match the root trigger's anchoring rule.
 * Frames the fix as a routine refresh rather than calling out the underlying cause
 * (the data is just stale — could pre-date PR #9189, could be the result of changing
 * the root trigger after children were saved, etc).
 *
 * Hidden when there are no repairs, when the viewer can't edit, or when the validator
 * hasn't yet had a chance to run against the expanded flow.
 *
 * During the refresh loop a non-dismissible modal blocks the canvas so the user can't
 * accidentally edit (and dirty autosave) right before the on-success page reload.
 */
export function RefreshTimingBanner({
	staleScheduleRepairs,
	rootTemplateId,
	canEdit,
	impersonationContext,
	onRefreshComplete,
}: RefreshTimingBannerProps) {
	// Suppress before we have an id so the hook's required `rootTemplateId: string`
	// contract holds and `relatedTemplateIds` is never undefined on the mutation.
	if (!canEdit || !rootTemplateId || staleScheduleRepairs.length === 0) {
		return null;
	}

	return (
		<RefreshTimingBannerInner
			staleScheduleRepairs={staleScheduleRepairs}
			rootTemplateId={rootTemplateId}
			impersonationContext={impersonationContext}
			onRefreshComplete={onRefreshComplete}
		/>
	);
}

function RefreshTimingBannerInner({
	staleScheduleRepairs,
	rootTemplateId,
	impersonationContext,
	onRefreshComplete,
}: {
	staleScheduleRepairs: StaleScheduleErrorWithRepair[];
	rootTemplateId: string;
	impersonationContext?: Api.IImpersonationContext;
	onRefreshComplete?: (result: { failedStepIds: string[] }) => void;
}) {
	const { status, completedCount, totalCount, failedStepIds, refresh } = useRefreshStaleSchedules({
		rootTemplateId,
		impersonationContext,
		onComplete: onRefreshComplete,
	});

	const handleRefresh = () => {
		void refresh(staleScheduleRepairs);
	};

	const handleRetry = () => {
		const remaining = staleScheduleRepairs.filter(error => failedStepIds.includes(error.repair.persistedStep.id));
		void refresh(remaining.length > 0 ? remaining : staleScheduleRepairs);
	};

	const isWorking = status === 'working';
	const isPartialFailure = status === 'partial-failure';
	const failedCount = failedStepIds.length;

	return (
		<>
			<div className={css(styleSheet.banner)} role='status' aria-live='polite'>
				<span className={css(styleSheet.icon)} aria-hidden='true'>
					✨
				</span>
				<div className={css(styleSheet.copy)}>
					{isPartialFailure ? (
						<span className={css(styleSheet.message)}>
							{failedCount === 1
								? "Couldn't refresh 1 step. Try again or contact support."
								: `Couldn't refresh ${failedCount} steps. Try again or contact support.`}
						</span>
					) : (
						<span className={css(styleSheet.message)}>
							This automation needs a quick refresh before it can publish.
						</span>
					)}
				</div>
				<div className={css(styleSheet.actions)}>
					{isPartialFailure ? (
						<Button onClick={handleRetry} disabled={isWorking}>
							Try again
						</Button>
					) : (
						<Button onClick={handleRefresh} disabled={isWorking}>
							Refresh now
						</Button>
					)}
				</div>
			</div>
			{/*
			  Non-dismissible modal during the refresh loop. dismissOnOutsideClick=false
			  blocks backdrop clicks; showCloseButton={false} hides the X. onOpenChange is a
			  no-op so ESC can't close it. The on-success page reload unmounts everything;
			  on partial failure the hook flips status back and the banner takes over.
			*/}
			<Dialog open={isWorking} onOpenChange={() => undefined} dismissOnOutsideClick={false}>
				<DialogPopup styles={[styleSheet.modal]} showCloseButton={false}>
					<DialogTitle>Refreshing step timing</DialogTitle>
					<DialogBody styles={[styleSheet.modalBody]}>
						<LoadingSpinner type='small' />
						<span className={css(styleSheet.modalProgress)}>
							Refreshing {completedCount} of {totalCount} {totalCount === 1 ? 'step' : 'steps'}…
						</span>
						<span className={css(styleSheet.modalHelp)}>
							Please don&apos;t close this tab. The page will reload when it finishes.
						</span>
					</DialogBody>
				</DialogPopup>
			</Dialog>
		</>
	);
}

const styleSheet = StyleSheet.create({
	banner: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		backgroundColor: THEME.colors.info.light,
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.info.main}`,
		margin: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	icon: {
		fontSize: THEME.typography.fontSizes.lg,
		flexShrink: 0,
	},
	copy: {
		flex: 1,
	},
	message: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		lineHeight: 1.4,
	},
	actions: {
		flexShrink: 0,
	},
	modal: {
		maxWidth: 420,
	},
	modalBody: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.lg}px ${THEME.spacing.md}px ${THEME.spacing.sm}px`,
		textAlign: 'center',
	},
	modalProgress: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	modalHelp: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		lineHeight: 1.4,
	},
});
